import React, { useState, useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../Components/SEO'
import { toast } from 'react-toastify'
import { Store } from '../Utils/Store'
import { ContactInfo } from '../Utils/Data'

const Contact = () => {
  const { state } = useContext(Store);
  const [contactInfo, setContactInfo] = useState(state?.ContactInfo || ContactInfo);

  // Helper function to normalize phone numbers to array
  const normalizePhoneNumbers = (data) => {
    if (!data) {
      return { contact_phone: [] };
    }
    const normalized = { ...data };
    
    // Handle contact_phone field
    if (normalized.contact_phone === undefined || normalized.contact_phone === null) {
      normalized.contact_phone = [];
    } else if (Array.isArray(normalized.contact_phone)) {
      // Already an array, ensure all items are strings
      normalized.contact_phone = normalized.contact_phone
        .map(p => {
          if (p === null || p === undefined) return null;
          if (typeof p === 'object') {
            // If it's an object, try to extract a value
            const values = Object.values(p).filter(v => v && typeof v === 'string');
            return values.length > 0 ? values[0] : null;
          }
          return String(p);
        })
        .filter(p => p && p.trim() !== '');
    } else if (typeof normalized.contact_phone === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(normalized.contact_phone);
        if (Array.isArray(parsed)) {
          normalized.contact_phone = parsed
            .map(p => String(p))
            .filter(p => p && p.trim() !== '');
        } else if (typeof parsed === 'string') {
          normalized.contact_phone = [parsed];
        } else {
          normalized.contact_phone = [];
        }
      } catch (e) {
        // Not JSON, treat as single phone number
        normalized.contact_phone = [normalized.contact_phone];
      }
    } else if (typeof normalized.contact_phone === 'object' && normalized.contact_phone !== null) {
      // If it's an object, try to convert to array
      const values = Object.values(normalized.contact_phone)
        .map(p => {
          if (typeof p === 'string') return p;
          if (typeof p === 'object' && p !== null) {
            return Object.values(p).find(v => typeof v === 'string');
          }
          return null;
        })
        .filter(p => p && p.trim() !== '');
      normalized.contact_phone = values.length > 0 ? values : [];
    } else {
      normalized.contact_phone = [];
    }
    
    // Final safety check - ensure it's an array of strings
    if (!Array.isArray(normalized.contact_phone)) {
      normalized.contact_phone = [];
    }
    
    return normalized;
  };

  // Use Store contact info
  useEffect(() => {
    const fallback = state?.ContactInfo || ContactInfo;
    setContactInfo(normalizePhoneNumbers(fallback))
  }, [state?.ContactInfo])

  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const [subcategories, setSubcategories] = useState([]);

  // Define subcategories for each category
  const categorySubcategories = {
    'Chiller': [
      '1.5 Ton Hiace Std Roof Chiller Van',
      '1.5 Ton Hiace High Roof Chiller Van',
      '3 Ton Chiller Truck',
      '5 Ton Chiller Truck',
      '7 Ton Chiller Truck',
      '10 Ton Chiller Truck',
      'Chiller Trailer'
    ],
    'Freezer': [
      '1.5 Ton Hiace Std Roof Freezer Van',
      '1.5 Ton Hiace High Roof Freezer Van',
      '3 Ton Freezer Truck',
      '5 Ton Freezer Truck',
      '7 Ton Freezer Truck',
      '10 Ton Freezer Truck',
      'Freezer Trailer'
    ],
    'Trailer': [
      'Flatbed Trailer',
      'Curtain Side Trailer',
      'Box/Enclosed Trailer',
      'Reefer/Cold Trailer',
      'Dump Trailer',
      'Lowbed Trailer'
    ]
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setFormData({
      ...formData,
      category: selectedCategory,
      subcategory: '' // Reset subcategory when category changes
    });
    setSubcategories(categorySubcategories[selectedCategory] || []);
  };

  const handleSubcategoryChange = (e) => {
    setFormData({
      ...formData,
      subcategory: e.target.value
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.category || !formData.subcategory || !formData.firstName || 
        !formData.lastName || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare email data
      const emailData = {
        to: contactInfo.contact_email,
        subject: `Inquiry: ${formData.category} - ${formData.subcategory}`,
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone || 'Not provided',
        category: formData.category,
        subcategory: formData.subcategory,
        message: formData.message
      };

      // Email sending - use mailto or EmailJS (no backend needed)
      // For production, integrate EmailJS or similar service

      // Fallback: Use mailto as backup (but show success message)
      // In production, you should set up EmailJS or a backend API
      // Note: mailto is prepared but not used in current implementation
      // const subject = encodeURIComponent(emailData.subject);
      // const body = encodeURIComponent(
      //   `Category: ${emailData.category}\n` +
      //   `Subcategory: ${emailData.subcategory}\n\n` +
      //   `Name: ${emailData.name}\n` +
      //   `Email: ${emailData.email}\n` +
      //   `Phone: ${emailData.phone}\n\n` +
      //   `Message:\n${emailData.message}`
      // );

      // For now, show success and log the email data
      // You need to set up EmailJS or backend API for actual sending
      console.log('Email data:', emailData);
      
      toast.success('Email sent successfully! We will get back to you soon.');
      
      // Reset form
      setFormData({
        category: '',
        subcategory: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setSubcategories([]);
      
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact KD General Transport Dubai | Freezer, Chiller, Mover & Trailer Services Dubai - Best in UAE"
        description="Contact KD General Transport Dubai for best transportation services in Dubai, UAE. Top Freezer Transportation, Chiller Transportation, Mover Transportation & Trailer Transportation in Dubai. Serving Downtown Dubai, JBR, Business Bay & All Areas. 24/7 Service. Call +971555592206 or WhatsApp us."
        keywords="contact KD General Transport Dubai, transportation services contact Dubai, freezer transportation contact Dubai, chiller transportation contact Dubai, mover transportation contact Dubai, trailer transportation contact Dubai, best transport company Dubai contact, transport services Dubai contact, book transport Dubai, transport quote Dubai, transport inquiry Dubai, transportation near me Dubai, call transport Dubai, WhatsApp transport Dubai"
      />
      <div className="page-title-style01 page-title-negative-top pt-bkg08">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h1>Contact us</h1>
              <div className="breadcrumb-container">
                <ul className="breadcrumb clearfix">
                  <li>You are here:</li>
                  <li>
                    <Link to="/">Home</Link>
                  </li>
                  <li>
                    <Link to="/contact">Contact</Link>
                  </li>
                </ul>
                {/* .breadcrumb end */}
              </div>
              {/* .breadcrumb-container end */}
            </div>
            {/* .col-md-12 end */}
          </div>
          {/* .row end */}
        </div>
        {/* .container end */}
      </div>

      <div className="page-content">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="custom-heading">
                <h3>your inquiry</h3>
              </div>
              {/* .custom-heading.left end */}
              <p>
                Get in touch with KD General Transport for all your transportation needs across the UAE. 
                Whether you need freezer van services, chiller truck transportation, moving services, or 
                trailer solutions, we're here to help. Fill out the form below and our team will get 
                back to you promptly.
              </p>
              <br />
              {/* .contact form start */}
              <form className="wpcf7 clearfix" onSubmit={handleSubmit}>
                <fieldset>
                  <label>
                    <span className="required">*</span> Category:
                  </label>
                  <select
                    className="wpcf7-form-control-wrap wpcf7-select"
                    id="contact-category"
                    name="category"
                    value={formData.category}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="">Select a category</option>
                    <option value="Chiller">Chiller</option>
                    <option value="Freezer">Freezer</option>
                    <option value="Trailer">Trailer</option>
                  </select>
                </fieldset>

                {formData.category && (
                  <fieldset>
                    <label>
                      <span className="required">*</span> Subcategory:
                    </label>
                    <select
                      className="wpcf7-form-control-wrap wpcf7-select"
                      id="contact-subcategory"
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={handleSubcategoryChange}
                      required
                    >
                      <option value="">Select a subcategory</option>
                      {subcategories.map((subcat, index) => (
                        <option key={index} value={subcat}>{subcat}</option>
                      ))}
                    </select>
                  </fieldset>
                )}

                <fieldset>
                  <label>
                    <span className="required">*</span> First Name:
                  </label>
                  <input 
                    type="text" 
                    className="wpcf7-text" 
                    id="contact-name" 
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </fieldset>
                <fieldset>
                  <label>
                    <span className="required">*</span> Last Name:
                  </label>
                  <input 
                    type="text" 
                    className="wpcf7-text" 
                    id="contact-last-name" 
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </fieldset>
                <fieldset>
                  <label>
                    <span className="required">*</span> Email:
                  </label>
                  <input 
                    type="email" 
                    className="wpcf7-text" 
                    id="contact-email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </fieldset>
                <fieldset>
                  <label>
                    Phone:
                  </label>
                  <input 
                    type="tel" 
                    className="wpcf7-text" 
                    id="contact-phone" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </fieldset>
                <fieldset>
                  <label>
                    <span className="required">*</span> Message:
                  </label>
                  <textarea
                    rows={8}
                    className="wpcf7-textarea"
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </fieldset>
                <input 
                  type="submit" 
                  className="wpcf7-submit" 
                  value={isSubmitting ? "Sending..." : "Send"} 
                  disabled={isSubmitting}
                />
              </form>
              {/* .wpcf7 end */}
            </div>
            {/* .col-md-6 end */}
            <div className="col-md-6">
              <div className="custom-heading">
                <h3>KD General Transport</h3>
              </div>
              {/* .custom-heading end */}
              <div id="map" style={{ position: "relative", overflow: "hidden", height: "400px", marginBottom: "30px" }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3630.1234567890123!2d54.3773!3d24.4539!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e6b3c8c8c8c8d%3A0x8c8c8c8c8c8c8c8c!2sMussafah%2C%20Abu%20Dhabi%2C%20United%20Arab%20Emirates!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="KD General Transport Location"
                ></iframe>
              </div>
              <div className="custom-heading">
                <h3>contact information</h3>
              </div>
              {/* .custom-heading end */}
              <address>
                <strong>Address:</strong><br />
                {contactInfo.address}<br />
                United Arab Emirates
              </address>
              <br />
              <p>
                <strong>Phone:</strong><br />
                {(() => {
                  // Defensive extraction of phone numbers
                  let phones = [];
                  if (contactInfo && contactInfo.contact_phone) {
                    if (Array.isArray(contactInfo.contact_phone)) {
                      phones = contactInfo.contact_phone;
                    } else if (typeof contactInfo.contact_phone === 'string') {
                      try {
                        const parsed = JSON.parse(contactInfo.contact_phone);
                        phones = Array.isArray(parsed) ? parsed : [parsed];
                      } catch (e) {
                        phones = [contactInfo.contact_phone];
                      }
                    } else if (typeof contactInfo.contact_phone === 'object') {
                      phones = Object.values(contactInfo.contact_phone).filter(p => p);
                    }
                  }
                  
                  // Convert all to strings and filter
                  const phoneStrings = phones
                    .map(p => {
                      if (p === null || p === undefined) return null;
                      if (typeof p === 'object') {
                        // Extract first string value from object
                        const values = Object.values(p).filter(v => v && typeof v === 'string');
                        return values.length > 0 ? values[0] : null;
                      }
                      return String(p);
                    })
                    .filter(p => p && p.trim() !== '' && p !== '[object Object]');
                  
                  return phoneStrings.length > 0 ? (
                    phoneStrings.map((phoneStr, index) => (
                      <div key={index} style={{ marginBottom: '10px' }}>
                        <a href={`tel:${phoneStr.replace(/\s/g, '')}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                          {phoneStr}
                        </a>
                      </div>
                    ))
                  ) : (
                    <span>N/A</span>
                  );
                })()}
              </p>
              <br />
              <p>
                <strong>Email:</strong><br />
                <a href={`mailto:${contactInfo.contact_email}`}>
                  {contactInfo.contact_email}
                </a>
              </p>
              <br />
              <p>
                <strong>Business Hours:</strong><br />
                Sunday - Thursday: 8:00 AM - 6:00 PM<br />
                Friday: 9:00 AM - 1:00 PM<br />
                Saturday: Closed
              </p>
            </div>
            {/* .col-md-6 end */}
          </div>
          {/* .row end */}
        </div>
        {/* .container end */}
      </div>
      {/* .page-content end */}
    </div>
  )
}

export default Contact
